import TreeNodeType from "neon-frontend-utils/src/components/TreeView/components/TreeView/constants/TreeNodeType";
import Logger from "neon-frontend-utils/src/logger";
import Utils from "neon-frontend-utils/src/utils";
import AppConfig from "../../../../../App.config";
import Components from "../../../../../constants/Components";
import QuestionComponents from "../constants/QuestionComponents";
import * as actionType from "./../constants/ActionTypes";

const initialState = {
    questionnaireContent: {
        handle: null,
        item: {
            handle: null,
            name: null,
        },
        name: null,
        translations: {
            choices: [],
            content: [],
            feedback: [],
        },
    },
    selectedId: null,
    treeNodesV2: {},
};

// BE does not provide language identifiers for empty/missing translations, so FE is leading here
const languages = ["nl_NL", "en_GB", "de_DE", "fr_FR"];

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {{}} new state
 */
export default function questionnaireBuilderReducer(state = initialState, action) {
    const newState = Object.assign({}, state);

    switch (action.type) {
        case actionType.TOGGLE_BUILDER_NODE: {
            try {
                // reset all previous selections
                Object.keys(newState.treeNodesV2).forEach((node) => {
                    newState.treeNodesV2[node].isSelected = false;
                });

                // set the selected bit on the opened (sub)menu node (it needs to deal with both name and id)
                if (action.node && action.node.name && newState.treeNodesV2[action.node.name]) {
                    // root node
                    newState.treeNodesV2[action.node.name].isOpen =
                        newState.treeNodesV2[action.node.name].isOpen !== true;
                    newState.treeNodesV2[action.node.name].isSelected =
                        newState.treeNodesV2[action.node.name].isSelected !== true;
                } else if (action.node && action.node.id && newState.treeNodesV2[action.node.id]) {
                    // child node (parent node needs to stay open)
                    newState.treeNodesV2[action.node.id].isSelected =
                        newState.treeNodesV2[action.node.id].isSelected !== true;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.QUESTIONNAIRE_BUILDER,
                    error,
                    message: "Exception in questionnaire builder reducer TOGGLE_BUILDER_NODE",
                });
            }

            break;
        }

        case actionType.GET_BUILDER_QUESTIONNAIRES: {
            try {
                const pathData = {};

                // register all root paths
                if (action.response && action.response.length) {
                    action.response.forEach((questionnaire) => {
                        pathData[questionnaire.name] = {
                            children: [],
                            endNode: false,
                            handle: questionnaire.handle,
                            id: questionnaire.id,
                            isAbilityTest: questionnaire.group?.isAbilityTest || false,
                            isOpen: false,
                            isRoot: true,
                            name: questionnaire.name,
                            path: questionnaire.name,
                            type: TreeNodeType.FOLDER,
                        };
                    });
                }

                newState.treeNodesV2 = pathData;
            } catch (error) {
                Logger.instance.error({
                    component: Components.QUESTIONNAIRE_BUILDER,
                    error,
                    message: "Exception in questionnaire builder reducer GET_BUILDER_QUESTIONNAIRES",
                });
            }

            break;
        }

        case actionType.GET_BUILDER_QUESTIONS: {
            try {
                // sort the response by name
                const sortedQuestions = Utils.alphabeticallySortFieldInArray(action.response.questions, "handle");
                const sortedTexts = Utils.alphabeticallySortFieldInArray(action.response.texts, "handle");

                // clone state (dont clear it, child nodes are added to it)
                newState.treeNodesV2 = Object.assign({}, state.treeNodesV2);

                const constructNode = (item, path, type) => ({
                    children: [],
                    deactivated: !!item.deactivatedOn,
                    endNode: true,
                    id: path,
                    isAbilityTest: action.node.isAbilityTest, // to distinct (inside the question) between text and image fields
                    isOpen: false,
                    isRoot: false,
                    isSelected: false,
                    ltpId: item.id,
                    name: item.calculationName || item.handle,
                    parentItem: {
                        handle: action.node.handle,
                        id: action.node.id, // v2 requires the parent id to save its data to
                        name: action.node.name,
                    },

                    path,
                    type, // to distinct between question and text
                    uuid: item.id,
                });

                // supporting texts (ie instructions)
                sortedTexts.forEach((text) => {
                    const noTranslations =
                        (Array.isArray(text.translations) && !text.translations.length) ||
                        !Object.keys(text.translations).length;

                    if (noTranslations) {
                        return;
                    }

                    const parentNode = newState.treeNodesV2[action.path];
                    const path = `${parentNode.path}/text/${text.id}`; // path to newly received child node

                    // populate the children array of the parent node, if not already present
                    if (!parentNode.children.includes(path)) {
                        parentNode.children.push(path);
                    }

                    newState.treeNodesV2[path] = constructNode(text, path, QuestionComponents.QUESTION_TEXT);
                });

                // actual questions
                sortedQuestions.forEach((node) => {
                    if (
                        (!node.translations && !node.choices && !node.texts) ||
                        (!node.texts && !node.choices && node.translations && !node.translations.length) ||
                        (!node.translations && !node.choices && node.texts && !node.texts.length) ||
                        (!node.choices && !node.translations && node.texts && !node.choices.length)
                    ) {
                        // this node has either no choice, translations or texts, or invalid translation for one of those
                        return;
                    }

                    const parentNode = newState.treeNodesV2[action.path];
                    const path = `${parentNode.path}/question/${node.id}`; // path to newly received child node

                    // populate the children array of the parent node, if not already present
                    if (!parentNode.children.includes(path)) {
                        parentNode.children.push(path);
                    }

                    newState.treeNodesV2[path] = constructNode(node, path, QuestionComponents.QUESTION);
                });

                // highlight the root node and set its isOpen property
                Object.keys(state.treeNodesV2).forEach((node) => {
                    state.treeNodesV2[node].isOpen = action.id === state.treeNodesV2[node].id;
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.QUESTIONNAIRE_BUILDER,
                    error,
                    message: "Exception in questionnaire builder reducer GET_BUILDER_QUESTIONS",
                });
            }

            break;
        }

        case actionType.GET_BUILDER_QUESTION_CONTENT: {
            try {
                const name = action.item.name;
                const handle = action.questionnaireContent.handle;

                newState.questionnaireContent = {
                    ...action.questionnaireContent, // ensure all original keys are present, required for saving
                    handle: action.questionnaireContent.handle,

                    item: {
                        handle: handle || "",
                        name: name || "",
                    },
                    name: action.item.parentItem.name || "",
                    translations: {
                        choices: [],
                        content: [],
                        feedback: [],
                    },
                };

                const getQuestionConfigForLanguage = (lang, langIndex, type) => {
                    const bucket = AppConfig.global.s3Bucket;

                    const getTextValue = (translation, contentType) => {
                        switch (contentType) {
                            case QuestionComponents.QUESTION:
                                if (translation && translation[lang] && translation[lang].description) {
                                    return translation[lang].description.replace("{{bucket}}", bucket);
                                }
                                break;
                            case QuestionComponents.QUESTION_TEXT:
                                if (translation && translation[lang] && translation[lang].text) {
                                    return translation[lang].text.replace("{{bucket}}", bucket);
                                }
                                break;
                            default:
                                return "";
                        }

                        return "";
                    };

                    const textValue = getTextValue(action.questionnaireContent.translations, type);

                    return {
                        language: lang,
                        translationKey: `question_${action.questionnaireContent.handle}_${langIndex}`,
                        type,
                        value: textValue || "",
                    };
                };

                const getChoiceConfigForLanguage = (
                    keyName,
                    translationObject,
                    language,
                    choiceId,
                    groupId = null,
                ) => ({
                    groupId,
                    id: choiceId,
                    language: language || null,
                    translationKey: keyName,
                    value: translationObject?.label || "",
                });

                const getFeedbackConfigForLanguage = (lang, langIndex, choiceId) => {
                    const bucket = AppConfig.global.s3Bucket;

                    let explanationCorrect = "";
                    let explanationIncorrect = "";
                    let explanationUnanswered = "";

                    explanationCorrect =
                        action.questionnaireContent.translations[lang]?.explanationCorrect?.replace(
                            "{{bucket}}",
                            bucket,
                        ) || "";
                    explanationIncorrect =
                        action.questionnaireContent.translations[lang]?.explanationIncorrect?.replace(
                            "{{bucket}}",
                            bucket,
                        ) || "";
                    explanationUnanswered =
                        action.questionnaireContent.translations[lang]?.explanationUnanswered?.replace(
                            "{{bucket}}",
                            bucket,
                        ) || "";

                    return {
                        explanationCorrect,
                        explanationIncorrect,
                        explanationUnanswered,
                        id: choiceId,
                        language: lang,
                        translationKey: `question_${action.questionnaireContent.handle}_${langIndex}`,
                    };
                };

                // parse questions
                if (action.questionnaireContent.translations) {
                    languages.forEach((lang, langIndex) => {
                        newState.questionnaireContent.translations.content.push(
                            getQuestionConfigForLanguage(lang, langIndex, action.item.type),
                        );
                    });
                }

                // parse choice texts and images
                if (
                    action.questionnaireContent &&
                    action.questionnaireContent.choices &&
                    action.questionnaireContent.choices.length
                ) {
                    action.questionnaireContent.choices.forEach((choiceSet, choiceIndex) => {
                        // init choice array for this choiceSet
                        if (!newState.questionnaireContent.translations.choices[choiceIndex]) {
                            newState.questionnaireContent.translations.choices[choiceIndex] = [];
                        }

                        if (choiceSet && Object.keys(choiceSet).length) {
                            if (choiceSet.translations && Object.keys(choiceSet.translations).length) {
                                // construct the translationKey (only used as an identifier, not by Lokalise)
                                const constructedLangKey = `answer_${action.questionnaireContent.handle}_${choiceIndex}`;

                                // note: local languages have deliberately been made leading so empty entries are generated, which can be filled in
                                languages.forEach((langKey) => {
                                    const storedTranslations = choiceSet.translations[langKey] || null;
                                    const choiceConfig = getChoiceConfigForLanguage(
                                        constructedLangKey,
                                        storedTranslations,
                                        langKey,
                                        choiceSet.id,
                                        choiceSet.groupId,
                                    );

                                    // ability tests always have an image tag (even when unset), so the image upload form is rendered
                                    // the only exception is verbal analogies, these have a groupId and never use images (they do have the groupId to distinct left & right)
                                    if (action.item.isAbilityTest && !choiceSet.groupId) {
                                        choiceConfig.image = choiceSet.translations[langKey]?.image || null;
                                    }

                                    newState.questionnaireContent.translations.choices[choiceIndex].push(choiceConfig);
                                });
                            } else {
                                if (typeof choiceSet === "string") {
                                    throw new Error(
                                        `Encountered unexpected string value for choiceset, ${newState.questionnaireContent.id}`,
                                    );
                                }
                            }
                        }
                    });
                }

                // parse feedback texts
                if (action.questionnaireContent.isExampleQuestion) {
                    newState.questionnaireContent.translations.feedback = [];

                    languages.forEach((lang, langIndex) => {
                        newState.questionnaireContent.translations.feedback.push(
                            getFeedbackConfigForLanguage(lang, langIndex, action.questionnaireContent.id),
                        );
                    });
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.QUESTIONNAIRE_BUILDER,
                    error,
                    message: "Exception in questionnaire builder reducer GET_BUILDER_QUESTION_CONTENT",
                });
            }

            break;
        }

        case actionType.CLEAR_BUILDER_QUESTIONNAIRE_CONTENT: {
            newState.questionnaireContent = {
                handle: null,
                item: {
                    handle: null,
                    name: null,
                },
                name: null,
                translations: {
                    choices: [],
                    content: [],
                    feedback: [],
                },
            };

            break;
        }

        case actionType.CLEAR_BUILDER_TREE_NODES: {
            newState.treeNodesV2 = {};
            newState.selectedId = null;

            break;
        }

        case actionType.UPDATE_BUILDER_TRANSLATION_IMAGE: {
            try {
                // clone state objects
                newState.questionnaireContent = Object.assign({}, newState.questionnaireContent);
                newState.questionnaireContent.translations = Object.assign(
                    {},
                    newState.questionnaireContent.translations,
                );

                // go through translations to find the right one
                newState.questionnaireContent.translations.choices.forEach((choiceSet) => {
                    if (choiceSet && choiceSet.length) {
                        choiceSet.forEach((translation) => {
                            if (
                                translation.translationKey === action.translation.translationKey &&
                                translation.language === action.translation.languageIso
                            ) {
                                if (~translation.translationKey.indexOf("answer_") && action.translation.url) {
                                    translation.image = action.translation.url;
                                } else {
                                    throw new Error(
                                        "could not set the url to the newly uploaded image as the translation image value!",
                                    );
                                }
                            }
                        });
                    }
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.QUESTIONNAIRE_BUILDER,
                    error,
                    message: "Exception in questionnaire builder reducer UPDATE_BUILDER_TRANSLATION_IMAGE",
                });
            }

            break;
        }

        default:
            break;
    }

    return newState;
}
